import { useEffect, useState } from "react";
import { useTheme } from "../../../components/hooks/useTheme";
import { GridColDef } from "@mui/x-data-grid";
import { Link } from "react-router-dom";
import { Divider, Menu, MenuItem, Stack } from "@mui/material";
import { CustomDataGrid } from "../../../components/common/CustomDataGrid";
import baseURL from "../../../configs/api";
import { bookingsStatusColorCode } from "../../../utils/statusColorCode";
import Cookies from "js-cookie";
import useTenant from "../../../components/hooks/useTenant";
import useGeneral from "../../../components/hooks/useGeneral";
import moment from "moment";

type ReservationsListProps = {
    activeIndex: number;
    activeTabLable: string;
    paginationModel: any;
    setPaginationModel: (value: any) => void;
    searchValue: string;
    setSearchValue: (value: string) => void;
    reservationFilterValue: number;
    setReservationFilterValue: (value: number) => void;
    selectedFilter: string;
    setSelectedFilter: (value: string) => void;
    filterItems: any[]
}

export const ReservationList = ({
    activeIndex,
    activeTabLable,
    paginationModel,
    setPaginationModel,
    searchValue,
    setSearchValue,
    reservationFilterValue,
    setReservationFilterValue,
    selectedFilter,
    setSelectedFilter,
    filterItems
}: ReservationsListProps) => {
    const tenantId = Cookies.get("tenantId-jj-rental-ui") || "";
    const { themeStyles } = useTheme();
    const { handleTenantError }: any = useTenant();
    const { dateFormat }: any = useGeneral();
    const [loading, setLoading] = useState(true);
    const [searchTableData, setSearchTableData] = useState<any[]>([]);
    const [totalLength, setTotalLength] = useState<number>(0);
    const [isFirstTime, setIsFirstTime] = useState<boolean>(true);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const [columnFilterAnchorEl, setColumnFilterAnchorEl] = useState<null | HTMLElement>(null);
    const columnFilterOpen = Boolean(columnFilterAnchorEl);
    const initialColumns: GridColDef[] = [
        {
            field: "bookingNumber",
            headerName: "#",
            minWidth: 300,
            flex: 1,
            editable: false,
            sortable: true,
            align: "left",
            renderCell: (params) => {
                return (
                    <Link to={`/carrental/reservations/editreservation?id=${params.id}`}
                        style={{
                            color: "var(--app-link-color)",
                            textDecoration: "underline",
                        }}
                    >
                        {params?.value}
                    </Link>
                );
            },
        },
        {
            field: "customerName",
            headerName: "Customer",
            minWidth: 300,
            flex: 1,
            align: "left",
            editable: false,
            sortable: true,
        },
        {
            field: "pickupDate",
            headerName: "Pickup Date",
            minWidth: 300,
            flex: 1,
            align: "left",
            editable: false,
            sortable: true,
        },
        {
            field: "returnDate",
            headerName: "Return Date",
            minWidth: 300,
            flex: 1,
            align: "left",
            editable: false,
            sortable: true,
        },
        {
            field: "pickupLocationName",
            headerName: "Pickup Location",
            editable: false,
            sortable: true,
            minWidth: 300,
            flex: 1,
            align: "left"
        },
        {
            field: "createdBy",
            headerName: "Created By",
            editable: false,
            sortable: true,
            minWidth: 300,
            flex: 1,
            align: "left"
        },
        {
            field: "completedBy",
            headerName: "Completed By",
            editable: false,
            sortable: true,
            minWidth: 300,
            flex: 1,
            align: "left"
        },

        {
            field: "vehicleClassName",
            headerName: "Vehicle Class",
            minWidth: 300,
            flex: 1,
            align: "left",
            editable: false,
            sortable: true,
        },
        {
            field: "vehicleName",
            headerName: "Vehicle",
            minWidth: 300,
            flex: 1,
            align: "left",
            editable: false,
            sortable: true,
        },
        {
            field: "localAddress",
            headerName: "Local Address",
            minWidth: 300,
            flex: 1,
            align: "left",
            editable: false,
            sortable: true,
        },
        {
            field: "notes",
            headerName: "Notes",
            minWidth: 300,
            flex: 1,
            align: "left",
            editable: false,
            sortable: true,
        },
        {
            field: "status",
            headerName: "Status",
            headerClassName: themeStyles.table.headerBg,
            editable: false,
            sortable: true,
            minWidth: 150,
            flex: 1,
            align: "left",
            renderCell: (params) => {
                let color = {
                    colorBg: "#000000",
                    colorText: "#000000"
                };
                color = bookingsStatusColorCode?.find((color: any) => color?.label === params?.value?.trim()?.toLowerCase()) || {
                    colorBg: "#000000",
                    colorText: "#000000"
                };

                return (
                    <div className="flex items-end h-full">
                        <span className={`inline-flex text-xs px-[10px] py-[2px] rounded-xl font-semibold ${color.colorBg} ${color.colorText}`}>
                            {params.value}
                        </span>
                    </div>
                );
            }

        },
        {
            field: "totalPrice",
            headerName: "Total Price",
            minWidth: 100,
            flex: 1,
            align: "left",
            editable: false,
            sortable: true,
            renderCell: (params) => {
                return (
                    <span>${params.value}</span>
                )
            }
        },
        {
            field: "totalRevenue",
            headerName: "Total Revenue",
            minWidth: 100,
            flex: 1,
            align: "left",
            editable: false,
            sortable: true,
            renderCell: (params) => {
                return (
                    <span>${params.value}</span>
                )
            }
        },
        {
            field: "totalPaid",
            headerName: "Total Paid",
            minWidth: 100,
            flex: 1,
            align: "left",
            editable: false,
            sortable: true,
            renderCell: (params) => {
                return (
                    <span>${params.value}</span>
                )
            }
        },
        {
            field: "totalRefunded",
            headerName: "Total Refunded",
            minWidth: 100,
            flex: 1,
            align: "left",
            editable: false,
            sortable: true,
            renderCell: (params) => {
                return (
                    <span>${params.value}</span>
                )
            }
        },
        {
            field: "outstandingBalance",
            headerName: "Outstanding Balance",
            minWidth: 100,
            flex: 1,
            align: "left",
            editable: false,
            sortable: true,
            renderCell: (params) => {
                return (
                    <span>${params.value}</span>
                )
            }
        },
    ];
    const [columns, setColumns] = useState<GridColDef[]>(initialColumns);
    const [selectedColumns, setSelectedColumns] = useState(initialColumns?.map(col => col?.headerName));

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleColumnFilterClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setColumnFilterAnchorEl(event.currentTarget);
    };
    const handleColumnFilterClose = () => {
        setColumnFilterAnchorEl(null);
    };

    const toggleColumn = (headerName: string) => {
        setSelectedColumns(prev =>
            prev.includes(headerName) ? prev.filter(col => col !== headerName) : [...prev, headerName]
        );
    };

    useEffect(() => {
        const filterCol = initialColumns?.filter(col => selectedColumns?.includes(col?.headerName));
        setColumns(filterCol);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedColumns])

    const toggleAll = () => {
        if (selectedColumns?.length === initialColumns?.length) {
            setSelectedColumns([]);
        } else {
            setSelectedColumns(initialColumns?.map(col => col?.headerName));
        }
    };

    useEffect(() => {
        if (searchValue === "" && !isFirstTime) {
            const newPaginationModel = {
                page: 0,
                pageSize: paginationModel.pageSize,
            };

            setPaginationModel(newPaginationModel);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchValue]);

    useEffect(() => {
        if (searchValue === "") {
            setIsFirstTime(false);
            emptySearchApiCall(paginationModel);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [paginationModel])

    const emptySearchApiCall = (newPaginationModel: any) => {
        setLoading(true);
        if (activeIndex === 0) {
            fetch(`${baseURL}reservation/GetReservationsReportPaging/${newPaginationModel.page},${newPaginationModel.pageSize}`, {
                method: "GET",
                headers: {
                    'X-Tenant-ID': tenantId,
                    "Content-Type": "application/json"
                },
            })
                .then((res) => res.json())
                .then((data) => {
                    const result = data.reservationReport.map((item: any) => {
                        return {
                            ...item,
                            pickupDate: item.pickupDate
                                ? moment(new Date(item.pickupDate)).format(dateFormat + " hh:mm a")
                                : "",
                            returnDate: item.returenDate
                                ? moment(new Date(item.returenDate)).format(dateFormat + " hh:mm a")
                                : "",
                            totalPrice: item.totalPrice,
                            totalRevenue: item.totalRevenue,
                            totalPaid: item.totalPaid,
                            totalRefunded: item.totalRefunded,
                            outstandingBalance: item.outstandingBalance,
                        };
                    })

                    setSearchTableData(result);
                    setTotalLength(data.count);
                    setLoading(false);
                    handleTenantError(null);
                })
                .catch((error) => {
                    handleTenantError(error);
                    setSearchTableData([]);
                    setTotalLength(0)
                    setLoading(false);
                });
        } else if (activeIndex === 1) {
            const todayDate = new Date().toISOString().split("T")[0];
            fetch(`${baseURL}reservation/GetReservationsReportWithPickupDatePaging/${todayDate},${newPaginationModel.page},${newPaginationModel.pageSize}`, {
                method: "GET",
                headers: {
                    'X-Tenant-ID': tenantId,
                    "Content-Type": "application/json"
                },
            })
                .then((res) => res.json())
                .then((data) => {
                    const result = data.reservationReport.map((item: any) => {
                        return {
                            ...item,
                            pickupDate: item.pickupDate
                                ? moment(new Date(item.pickupDate)).format(dateFormat + " hh:mm a")
                                : "",
                            returnDate: item.returenDate
                                ? moment(new Date(item.returenDate)).format(dateFormat + " hh:mm a")
                                : "",
                            totalPrice: item.totalPrice,
                            totalRevenue: item.totalRevenue,
                            totalPaid: item.totalPaid,
                            totalRefunded: item.totalRefunded,
                            outstandingBalance: item.outstandingBalance,
                        };
                    })

                    setSearchTableData(result);
                    setTotalLength(data.count);
                    setLoading(false);
                    handleTenantError(null);
                })
                .catch((error) => {
                    handleTenantError(error);
                    setSearchTableData([]);
                    setTotalLength(0)
                    setLoading(false);
                });
        } else if (activeIndex === 2) {
            const todayDate = new Date().toISOString().split("T")[0];
            fetch(`${baseURL}reservation/GetReservationsReportWithReturnDatePaging/${todayDate},${newPaginationModel.page},${newPaginationModel.pageSize}`, {
                method: "GET",
                headers: {
                    'X-Tenant-ID': tenantId,
                    "Content-Type": "application/json"
                },
            })
                .then((res) => res.json())
                .then((data) => {
                    const result = data.reservationReport.map((item: any) => {
                        return {
                            ...item,
                            pickupDate: item.pickupDate
                                ? moment(new Date(item.pickupDate)).format(dateFormat + " hh:mm a")
                                : "",
                            returnDate: item.returenDate
                                ? moment(new Date(item.returenDate)).format(dateFormat + " hh:mm a")
                                : "",
                            totalPrice: item.totalPrice,
                            totalRevenue: item.totalRevenue,
                            totalPaid: item.totalPaid,
                            totalRefunded: item.totalRefunded,
                            outstandingBalance: item.outstandingBalance,
                        };
                    })


                    setSearchTableData(result);
                    setTotalLength(data.count);
                    setLoading(false);
                    handleTenantError(null);
                })
                .catch((error) => {
                    handleTenantError(error);
                    setSearchTableData([]);
                    setTotalLength(0)
                    setLoading(false);
                });
        } else if (activeIndex === 3) {
            const tomorrowDate = new Date(new Date().getTime() + 24 * 60 * 60 * 1000).toISOString().split("T")[0];
            fetch(`${baseURL}reservation/GetReservationsReportWithPickupDatePaging/${tomorrowDate},${newPaginationModel.page},${newPaginationModel.pageSize}`, {
                method: "GET",
                headers: {
                    'X-Tenant-ID': tenantId,
                    "Content-Type": "application/json"
                },
            })
                .then((res) => res.json())
                .then((data) => {
                    const result = data.reservationReport.map((item: any) => {
                        return {
                            ...item,
                            pickupDate: item.pickupDate
                                ? moment(new Date(item.pickupDate)).format(dateFormat + " hh:mm a")
                                : "",
                            returnDate: item.returenDate
                                ? moment(new Date(item.returenDate)).format(dateFormat + " hh:mm a")
                                : "",
                            totalPrice: item.totalPrice,
                            totalRevenue: item.totalRevenue,
                            totalPaid: item.totalPaid,
                            totalRefunded: item.totalRefunded,
                            outstandingBalance: item.outstandingBalance,
                        };
                    })

                    setSearchTableData(result);
                    setTotalLength(data.count);
                    setLoading(false);
                    handleTenantError(null);
                })
                .catch((error) => {
                    handleTenantError(error);
                    setSearchTableData([]);
                    setTotalLength(0)
                    setLoading(false);
                });
        } else if (activeIndex === 4) {
            const tomorrowDate = new Date(new Date().getTime() + 24 * 60 * 60 * 1000).toISOString().split("T")[0];
            fetch(`${baseURL}reservation/GetReservationsReportWithReturnDatePaging/${tomorrowDate},${newPaginationModel.page},${newPaginationModel.pageSize}`, {
                method: "GET",
                headers: {
                    'X-Tenant-ID': tenantId,
                    "Content-Type": "application/json"
                },
            })
                .then((res) => res.json())
                .then((data) => {
                    const result = data.reservationReport.map((item: any) => {
                        return {
                            ...item,
                            pickupDate: item.pickupDate
                                ? moment(new Date(item.pickupDate)).format(dateFormat + " hh:mm a")
                                : "",
                            returnDate: item.returenDate
                                ? moment(new Date(item.returenDate)).format(dateFormat + " hh:mm a")
                                : "",
                            totalPrice: item.totalPrice,
                            totalRevenue: item.totalRevenue,
                            totalPaid: item.totalPaid,
                            totalRefunded: item.totalRefunded,
                            outstandingBalance: item.outstandingBalance,
                        };
                    })


                    setSearchTableData(result);
                    setTotalLength(data.count);
                    setLoading(false);
                    handleTenantError(null);
                })
                .catch((error) => {
                    handleTenantError(error);
                    setSearchTableData([]);
                    setTotalLength(0)
                    setLoading(false);
                });
        } else if (activeTabLable === "Rental" || activeTabLable === "Completed" || activeTabLable === "Cancelled" || activeTabLable === "Open" || activeTabLable === "No Show") {
            fetch(`${baseURL}reservation/GetReservationsReportWithStatusPaging?status=${activeTabLable}&page=${newPaginationModel.page}&size=${newPaginationModel.pageSize}`, {
                method: "GET",
                headers: {
                    'X-Tenant-ID': tenantId,
                    "Content-Type": "application/json"
                },
            })
                .then((res) => res.json())
                .then((data) => {
                    const result = data.reservationReport.map((item: any) => {
                        return {
                            ...item,
                            pickupDate: item.pickupDate
                                ? moment(new Date(item.pickupDate)).format(dateFormat + " hh:mm a")
                                : "",
                            returnDate: item.returenDate
                                ? moment(new Date(item.returenDate)).format(dateFormat + " hh:mm a")
                                : "",
                            totalPrice: item.totalPrice,
                            totalRevenue: item.totalRevenue,
                            totalPaid: item.totalPaid,
                            totalRefunded: item.totalRefunded,
                            outstandingBalance: item.outstandingBalance,
                        };
                    })

                    setSearchTableData(result);
                    setTotalLength(data.count);
                    setLoading(false);
                    handleTenantError(null);
                })
                .catch((error) => {
                    handleTenantError(error);
                    setSearchTableData([]);
                    setTotalLength(0)
                    setLoading(false);
                });
        } else {
            fetch(`${baseURL}reservation/GetReservationsReportWithOutStandingPaymentPaging/${newPaginationModel.page},${newPaginationModel.pageSize}`, {
                method: "GET",
                headers: {
                    'X-Tenant-ID': tenantId,
                    "Content-Type": "application/json"
                },
            })
                .then((res) => res.json())
                .then((data) => {
                    const result = data.reservationReport.map((item: any) => {
                        return {
                            ...item,
                            pickupDate: item.pickupDate
                                ? moment(new Date(item.pickupDate)).format(dateFormat + " hh:mm a")
                                : "",
                            returnDate: item.returenDate
                                ? moment(new Date(item.returenDate)).format(dateFormat + " hh:mm a")
                                : "",
                            totalPrice: item.totalPrice,
                            totalRevenue: item.totalRevenue,
                            totalPaid: item.totalPaid,
                            totalRefunded: item.totalRefunded,
                            outstandingBalance: item.outstandingBalance,
                        };
                    })


                    setSearchTableData(result);
                    setTotalLength(data.count);
                    setLoading(false);
                    handleTenantError(null);
                })
                .catch((error) => {
                    handleTenantError(error);
                    setSearchTableData([]);
                    setTotalLength(0)
                    setLoading(false);
                });
        }
    }

    useEffect(() => {
        let timeOutId: NodeJS.Timeout;

        if (searchValue !== "") {
            timeOutId = setTimeout(() => {
                const newPaginationModel = {
                    page: 0,
                    pageSize: paginationModel.pageSize,
                };

                setPaginationModel(newPaginationModel);
            }, 1000);
        }

        return () => clearTimeout(timeOutId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchValue]);

    useEffect(() => {
        if (searchValue !== "") {
            apiCall(paginationModel);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [paginationModel])

    const apiCall = (newPaginationModel: any) => {
        let filterData: any[] = [];
        setLoading(true);
        if (activeIndex === 0) {
            switch (reservationFilterValue) {
                case 0:
                    fetch(`${baseURL}reservation/GetReservationsReportSearchWithCustomerName?customerName=${searchValue}&page=${newPaginationModel.page}&size=${newPaginationModel.pageSize}`, {
                        method: "GET",
                        headers: {
                            'X-Tenant-ID': tenantId,
                            "Content-Type": "application/json"
                        },
                    })
                        .then((res) => res.json())
                        .then((data) => {
                            filterData = data.reservationReport.map((item: any) => {
                                return {
                                    ...item,
                                    pickupDate: item.pickupDate
                                        ? moment(new Date(item.pickupDate)).format(dateFormat + " hh:mm a")
                                        : "",
                                    returnDate: item.returenDate
                                        ? moment(new Date(item.returenDate)).format(dateFormat + " hh:mm a")
                                        : "",
                                    totalPrice: item.totalPrice,
                                    totalRevenue: item.totalRevenue,
                                    totalPaid: item.totalPaid,
                                    totalRefunded: item.totalRefunded,
                                    outstandingBalance: item.outstandingBalance,
                                };
                            })


                            setSearchTableData(filterData);
                            setTotalLength(data.count);
                            setLoading(false);
                            handleTenantError(null);
                        })
                        .catch(error => {
                            setSearchTableData([]);
                            setTotalLength(0);
                            setLoading(false);
                            handleTenantError(error);
                        });
                    break;
                case 1:
                    fetch(`${baseURL}reservation/GetReservationsReportSearchWithBookingNumber?bookingNumber=${searchValue}&page=${newPaginationModel.page}&size=${newPaginationModel.pageSize}`, {
                        method: "GET",
                        headers: {
                            'X-Tenant-ID': tenantId,
                            "Content-Type": "application/json"
                        },
                    })
                        .then((res) => res.json())
                        .then((data) => {
                            filterData = data.reservationReport.map((item: any) => {
                                return {
                                    ...item,
                                    pickupDate: item.pickupDate
                                        ? moment(new Date(item.pickupDate)).format(dateFormat + " hh:mm a")
                                        : "",
                                    returnDate: item.returenDate
                                        ? moment(new Date(item.returenDate)).format(dateFormat + " hh:mm a")
                                        : "",
                                    totalPrice: item.totalPrice,
                                    totalRevenue: item.totalRevenue,
                                    totalPaid: item.totalPaid,
                                    totalRefunded: item.totalRefunded,
                                    outstandingBalance: item.outstandingBalance,
                                };
                            })


                            setSearchTableData(filterData);
                            setTotalLength(data.count);
                            setLoading(false);
                            handleTenantError(null);
                        })
                        .catch(error => {
                            setSearchTableData([]);
                            setTotalLength(0);
                            setLoading(false);
                            handleTenantError(error);
                        });
                    break;
            }
        } else if (activeIndex === 1) {
            const todayDate = new Date().toISOString().split("T")[0];
            switch (reservationFilterValue) {
                case 0:
                    fetch(`${baseURL}reservation/GetReservationsReportSearchWithCustomerNameAndPickupDate?customerName=${searchValue}&pickupDate=${todayDate}&page=${newPaginationModel.page}&size=${newPaginationModel.pageSize}`, {
                        method: "GET",
                        headers: {
                            'X-Tenant-ID': tenantId,
                            "Content-Type": "application/json"
                        },
                    })
                        .then((res) => res.json())
                        .then((data) => {
                            filterData = data.reservationReport.map((item: any) => {
                                return {
                                    ...item,
                                    pickupDate: item.pickupDate
                                        ? moment(new Date(item.pickupDate)).format(dateFormat + " hh:mm a")
                                        : "",
                                    returnDate: item.returenDate
                                        ? moment(new Date(item.returenDate)).format(dateFormat + " hh:mm a")
                                        : "",
                                    totalPrice: item.totalPrice,
                                    totalRevenue: item.totalRevenue,
                                    totalPaid: item.totalPaid,
                                    totalRefunded: item.totalRefunded,
                                    outstandingBalance: item.outstandingBalance,
                                };
                            })


                            setSearchTableData(filterData);
                            setTotalLength(data.count);
                            setLoading(false);
                            handleTenantError(null);
                        })
                        .catch(error => {
                            setSearchTableData([]);
                            setTotalLength(0);
                            setLoading(false);
                            handleTenantError(error);
                        });
                    break;
                case 1:
                    fetch(`${baseURL}reservation/GetReservationsReportSearchWithBookingNumberAndPickupDate?bookingNumber=${searchValue}&pickupDate=${todayDate}&page=${newPaginationModel.page}&size=${newPaginationModel.pageSize}`, {
                        method: "GET",
                        headers: {
                            'X-Tenant-ID': tenantId,
                            "Content-Type": "application/json"
                        },
                    })
                        .then((res) => res.json())
                        .then((data) => {
                            filterData = data.reservationReport.map((item: any) => {
                                return {
                                    ...item,
                                    pickupDate: item.pickupDate
                                        ? moment(new Date(item.pickupDate)).format(dateFormat + " hh:mm a")
                                        : "",
                                    returnDate: item.returenDate
                                        ? moment(new Date(item.returenDate)).format(dateFormat + " hh:mm a")
                                        : "",
                                    totalPrice: item.totalPrice,
                                    totalRevenue: item.totalRevenue,
                                    totalPaid: item.totalPaid,
                                    totalRefunded: item.totalRefunded,
                                    outstandingBalance: item.outstandingBalance,
                                };
                            })


                            setSearchTableData(filterData);
                            setTotalLength(data.count);
                            setLoading(false);
                            handleTenantError(null);
                        })
                        .catch(error => {
                            setSearchTableData([]);
                            setTotalLength(0);
                            setLoading(false);
                            handleTenantError(error);
                        });
                    break;
            }
        } else if (activeIndex === 2) {
            const todayDate = new Date().toISOString().split("T")[0];
            switch (reservationFilterValue) {
                case 0:
                    fetch(`${baseURL}reservation/GetReservationsReportSearchWithCustomerNameAndReturnDate?customerName=${searchValue}&returnDate=${todayDate}&page=${newPaginationModel.page}&size=${newPaginationModel.pageSize}`, {
                        method: "GET",
                        headers: {
                            'X-Tenant-ID': tenantId,
                            "Content-Type": "application/json"
                        },
                    })
                        .then((res) => res.json())
                        .then((data) => {
                            filterData = data.reservationReport.map((item: any) => {
                                return {
                                    ...item,
                                    pickupDate: item.pickupDate
                                        ? moment(new Date(item.pickupDate)).format(dateFormat + " hh:mm a")
                                        : "",
                                    returnDate: item.returenDate
                                        ? moment(new Date(item.returenDate)).format(dateFormat + " hh:mm a")
                                        : "",
                                    totalPrice: item.totalPrice,
                                    totalRevenue: item.totalRevenue,
                                    totalPaid: item.totalPaid,
                                    totalRefunded: item.totalRefunded,
                                    outstandingBalance: item.outstandingBalance,
                                };
                            })


                            setSearchTableData(filterData);
                            setTotalLength(data.count);
                            setLoading(false);
                            handleTenantError(null);
                        })
                        .catch(error => {
                            setSearchTableData([]);
                            setTotalLength(0);
                            setLoading(false);
                            handleTenantError(error);
                        });
                    break;
                case 1:
                    fetch(`${baseURL}reservation/GetReservationsReportSearchWithBookingNumberAndReturnDate?bookingNumber=${searchValue}&returnDate=${todayDate}&page=${newPaginationModel.page}&size=${newPaginationModel.pageSize}`, {
                        method: "GET",
                        headers: {
                            'X-Tenant-ID': tenantId,
                            "Content-Type": "application/json"
                        },
                    })
                        .then((res) => res.json())
                        .then((data) => {
                            filterData = data.reservationReport.map((item: any) => {
                                return {
                                    ...item,
                                    pickupDate: item.pickupDate
                                        ? moment(new Date(item.pickupDate)).format(dateFormat + " hh:mm a")
                                        : "",
                                    returnDate: item.returenDate
                                        ? moment(new Date(item.returenDate)).format(dateFormat + " hh:mm a")
                                        : "",
                                    totalPrice: item.totalPrice,
                                    totalRevenue: item.totalRevenue,
                                    totalPaid: item.totalPaid,
                                    totalRefunded: item.totalRefunded,
                                    outstandingBalance: item.outstandingBalance,
                                };
                            })


                            setSearchTableData(filterData);
                            setTotalLength(data.count);
                            setLoading(false);
                            handleTenantError(null);
                        })
                        .catch(error => {
                            setSearchTableData([]);
                            setTotalLength(0);
                            setLoading(false);
                            handleTenantError(error);
                        });
                    break;
            }
        } else if (activeIndex === 3) {
            const tomorrowDate = new Date(new Date().getTime() + 24 * 60 * 60 * 1000).toISOString().split("T")[0];
            switch (reservationFilterValue) {
                case 0:
                    fetch(`${baseURL}reservation/GetReservationsReportSearchWithCustomerNameAndPickupDate?customerName=${searchValue}&pickupDate=${tomorrowDate}&page=${newPaginationModel.page}&size=${newPaginationModel.pageSize}`, {
                        method: "GET",
                        headers: {
                            'X-Tenant-ID': tenantId,
                            "Content-Type": "application/json"
                        },
                    })
                        .then((res) => res.json())
                        .then((data) => {
                            filterData = data.reservationReport.map((item: any) => {
                                return {
                                    ...item,
                                    pickupDate: item.pickupDate
                                        ? moment(new Date(item.pickupDate)).format(dateFormat + " hh:mm a")
                                        : "",
                                    returnDate: item.returenDate
                                        ? moment(new Date(item.returenDate)).format(dateFormat + " hh:mm a")
                                        : "",
                                    totalPrice: item.totalPrice,
                                    totalRevenue: item.totalRevenue,
                                    totalPaid: item.totalPaid,
                                    totalRefunded: item.totalRefunded,
                                    outstandingBalance: item.outstandingBalance,
                                };
                            })


                            setSearchTableData(filterData);
                            setTotalLength(data.count);
                            setLoading(false);
                            handleTenantError(null);
                        })
                        .catch(error => {
                            setSearchTableData([]);
                            setTotalLength(0);
                            setLoading(false);
                            handleTenantError(error);
                        });
                    break;
                case 1:
                    fetch(`${baseURL}reservation/GetReservationsReportSearchWithBookingNumberAndPickupDate?bookingNumber=${searchValue}&pickupDate=${tomorrowDate}&page=${newPaginationModel.page}&size=${newPaginationModel.pageSize}`, {
                        method: "GET",
                        headers: {
                            'X-Tenant-ID': tenantId,
                            "Content-Type": "application/json"
                        },
                    })
                        .then((res) => res.json())
                        .then((data) => {
                            filterData = data.reservationReport.map((item: any) => {
                                return {
                                    ...item,
                                    pickupDate: item.pickupDate
                                        ? moment(new Date(item.pickupDate)).format(dateFormat + " hh:mm a")
                                        : "",
                                    returnDate: item.returenDate
                                        ? moment(new Date(item.returenDate)).format(dateFormat + " hh:mm a")
                                        : "",
                                    totalPrice: item.totalPrice,
                                    totalRevenue: item.totalRevenue,
                                    totalPaid: item.totalPaid,
                                    totalRefunded: item.totalRefunded,
                                    outstandingBalance: item.outstandingBalance,
                                };
                            })


                            setSearchTableData(filterData);
                            setTotalLength(data.count);
                            setLoading(false);
                            handleTenantError(null);
                        })
                        .catch(error => {
                            setSearchTableData([]);
                            setTotalLength(0);
                            setLoading(false);
                            handleTenantError(error);
                        });
                    break;
            }
        } else if (activeIndex === 4) {
            const tomorrowDate = new Date(new Date().getTime() + 24 * 60 * 60 * 1000).toISOString().split("T")[0];
            switch (reservationFilterValue) {
                case 0:
                    fetch(`${baseURL}reservation/GetReservationsReportSearchWithCustomerNameAndReturnDate?customerName=${searchValue}&returnDate=${tomorrowDate}&page=${newPaginationModel.page}&size=${newPaginationModel.pageSize}`, {
                        method: "GET",
                        headers: {
                            'X-Tenant-ID': tenantId,
                            "Content-Type": "application/json"
                        },
                    })
                        .then((res) => res.json())
                        .then((data) => {
                            filterData = data.reservationReport.map((item: any) => {
                                return {
                                    ...item,
                                    pickupDate: item.pickupDate
                                        ? moment(new Date(item.pickupDate)).format(dateFormat + " hh:mm a")
                                        : "",
                                    returnDate: item.returenDate
                                        ? moment(new Date(item.returenDate)).format(dateFormat + " hh:mm a")
                                        : "",
                                    totalPrice: item.totalPrice,
                                    totalRevenue: item.totalRevenue,
                                    totalPaid: item.totalPaid,
                                    totalRefunded: item.totalRefunded,
                                    outstandingBalance: item.outstandingBalance,
                                };
                            })


                            setSearchTableData(filterData);
                            setTotalLength(data.count);
                            setLoading(false);
                            handleTenantError(null);
                        })
                        .catch(error => {
                            setSearchTableData([]);
                            setTotalLength(0);
                            setLoading(false);
                            handleTenantError(error);
                        });
                    break;
                case 1:
                    fetch(`${baseURL}reservation/GetReservationsReportSearchWithBookingNumberAndReturnDate?bookingNumber=${searchValue}&returnDate=${tomorrowDate}&page=${newPaginationModel.page}&size=${newPaginationModel.pageSize}`, {
                        method: "GET",
                        headers: {
                            'X-Tenant-ID': tenantId,
                            "Content-Type": "application/json"
                        },
                    })
                        .then((res) => res.json())
                        .then((data) => {
                            filterData = data.reservationReport.map((item: any) => {
                                return {
                                    ...item,
                                    pickupDate: item.pickupDate
                                        ? moment(new Date(item.pickupDate)).format(dateFormat + " hh:mm a")
                                        : "",
                                    returnDate: item.returenDate
                                        ? moment(new Date(item.returenDate)).format(dateFormat + " hh:mm a")
                                        : "",
                                    totalPrice: item.totalPrice,
                                    totalRevenue: item.totalRevenue,
                                    totalPaid: item.totalPaid,
                                    totalRefunded: item.totalRefunded,
                                    outstandingBalance: item.outstandingBalance,
                                };
                            })


                            setSearchTableData(filterData);
                            setTotalLength(data.count);
                            setLoading(false);
                            handleTenantError(null);
                        })
                        .catch(error => {
                            setSearchTableData([]);
                            setTotalLength(0);
                            setLoading(false);
                            handleTenantError(error);
                        });
                    break;
            }
        } else if (activeTabLable === "Rental" || activeTabLable === "Completed" || activeTabLable === "Cancelled" || activeTabLable === "Open" || activeTabLable === "No Show") {
            switch (reservationFilterValue) {
                case 0:
                    fetch(`${baseURL}reservation/GetReservationsReportSearchWithCustomerNameAndStatus?customerName=${searchValue}&status=${activeTabLable}&page=${newPaginationModel.page}&size=${newPaginationModel.pageSize}`, {
                        method: "GET",
                        headers: {
                            'X-Tenant-ID': tenantId,
                            "Content-Type": "application/json"
                        },
                    })
                        .then((res) => res.json())
                        .then((data) => {
                            filterData = data.reservationReport.map((item: any) => {
                                return {
                                    ...item,
                                    pickupDate: item.pickupDate
                                        ? moment(new Date(item.pickupDate)).format(dateFormat + " hh:mm a")
                                        : "",
                                    returnDate: item.returenDate
                                        ? moment(new Date(item.returenDate)).format(dateFormat + " hh:mm a")
                                        : "",
                                    totalPrice: item.totalPrice,
                                    totalRevenue: item.totalRevenue,
                                    totalPaid: item.totalPaid,
                                    totalRefunded: item.totalRefunded,
                                    outstandingBalance: item.outstandingBalance,
                                };
                            })


                            setSearchTableData(filterData);
                            setTotalLength(data.count);
                            setLoading(false);
                            handleTenantError(null);
                        })
                        .catch(error => {
                            setSearchTableData([]);
                            setTotalLength(0);
                            setLoading(false);
                            handleTenantError(error);
                        });
                    break;
                case 1:
                    fetch(`${baseURL}reservation/GetReservationsReportSearchWithBookingNumberAndStatus?bookingNumber=${searchValue}&status=${activeTabLable}&page=${newPaginationModel.page}&size=${newPaginationModel.pageSize}`, {
                        method: "GET",
                        headers: {
                            'X-Tenant-ID': tenantId,
                            "Content-Type": "application/json"
                        },
                    })
                        .then((res) => res.json())
                        .then((data) => {
                            filterData = data.reservationReport.map((item: any) => {
                                return {
                                    ...item,
                                    pickupDate: item.pickupDate
                                        ? moment(new Date(item.pickupDate)).format(dateFormat + " hh:mm a")
                                        : "",
                                    returnDate: item.returenDate
                                        ? moment(new Date(item.returenDate)).format(dateFormat + " hh:mm a")
                                        : "",
                                    totalPrice: item.totalPrice,
                                    totalRevenue: item.totalRevenue,
                                    totalPaid: item.totalPaid,
                                    totalRefunded: item.totalRefunded,
                                    outstandingBalance: item.outstandingBalance,
                                };
                            })


                            setSearchTableData(filterData);
                            setTotalLength(data.count);
                            setLoading(false);
                            handleTenantError(null);
                        })
                        .catch(error => {
                            setSearchTableData([]);
                            setTotalLength(0);
                            setLoading(false);
                            handleTenantError(error);
                        });
                    break;
            }
        } else {
            switch (reservationFilterValue) {
                case 0:
                    fetch(`${baseURL}reservation/GetReservationsReportSearchWithCustomerNameAndOutStandingPayment?customerName=${searchValue}&page=${newPaginationModel.page}&size=${newPaginationModel.pageSize}`, {
                        method: "GET",
                        headers: {
                            'X-Tenant-ID': tenantId,
                            "Content-Type": "application/json"
                        },
                    })
                        .then((res) => res.json())
                        .then((data) => {
                            filterData = data.reservationReport.map((item: any) => {
                                return {
                                    ...item,
                                    pickupDate: item.pickupDate
                                        ? moment(new Date(item.pickupDate)).format(dateFormat + " hh:mm a")
                                        : "",
                                    returnDate: item.returenDate
                                        ? moment(new Date(item.returenDate)).format(dateFormat + " hh:mm a")
                                        : "",
                                    totalPrice: item.totalPrice,
                                    totalRevenue: item.totalRevenue,
                                    totalPaid: item.totalPaid,
                                    totalRefunded: item.totalRefunded,
                                    outstandingBalance: item.outstandingBalance,
                                };
                            })


                            setSearchTableData(filterData);
                            setTotalLength(data.count);
                            setLoading(false);
                            handleTenantError(null);
                        })
                        .catch(error => {
                            setSearchTableData([]);
                            setTotalLength(0);
                            setLoading(false);
                            handleTenantError(error);
                        });
                    break;
                case 1:
                    fetch(`${baseURL}reservation/GetReservationsReportSearchWithBookingNumberAndOutStandingPayment?bookingNumber=${searchValue}&page=${newPaginationModel.page}&size=${newPaginationModel.pageSize}`, {
                        method: "GET",
                        headers: {
                            'X-Tenant-ID': tenantId,
                            "Content-Type": "application/json"
                        },
                    })
                        .then((res) => res.json())
                        .then((data) => {
                            filterData = data.reservationReport.map((item: any) => {
                                return {
                                    ...item,
                                    pickupDate: item.pickupDate
                                        ? moment(new Date(item.pickupDate)).format(dateFormat + " hh:mm a")
                                        : "",
                                    returnDate: item.returenDate
                                        ? moment(new Date(item.returenDate)).format(dateFormat + " hh:mm a")
                                        : "",
                                    totalPrice: item.totalPrice,
                                    totalRevenue: item.totalRevenue,
                                    totalPaid: item.totalPaid,
                                    totalRefunded: item.totalRefunded,
                                    outstandingBalance: item.outstandingBalance,
                                };
                            })


                            setSearchTableData(filterData);
                            setTotalLength(data.count);
                            setLoading(false);
                            handleTenantError(null);
                        })
                        .catch(error => {
                            setSearchTableData([]);
                            setTotalLength(0);
                            setLoading(false);
                            handleTenantError(error);
                        });
                    break;
            }
        }

    }

    return (
        <div className="w-full mt-2">
            <div className={`w-full border ${themeStyles.main.borderColor} rounded-[16px]`}>
                <div className={`flex justify-between items-center p-4 border-b ${themeStyles.main.borderColor}`}>
                    <div className="flex items-center gap-1.5">

                        <h2 className={`text-lg font-semibold ${themeStyles.main.textHeadingColor}`}>Reservations</h2>

                        <div >
                            <button
                                className={`flex items-center justify-between py-[5px] px-[12px] gap-2.5 rounded-lg border border-solid ${themeStyles.topBar.tailwindBorderColor}`}
                                aria-controls={columnFilterOpen ? 'basic-menu' : undefined}
                                aria-haspopup="true"
                                aria-expanded={columnFilterOpen ? 'true' : undefined}
                                onClick={handleColumnFilterClick}
                            >
                                <Stack
                                    direction="row"
                                    spacing={1}
                                    alignItems={"center"}
                                    justifyContent={"center"}
                                >
                                    <span className={`${themeStyles.topBar.tailwindTextColor} text-[14px] font-medium`}>Columns</span>
                                    <span className={`material-symbols-outlined text-[18px] font-light ${themeStyles.main.textHeadingColor}`}>
                                        filter_alt
                                    </span>
                                </Stack>
                            </button>

                            <Menu
                                id="basic-menu"
                                anchorEl={columnFilterAnchorEl}
                                open={columnFilterOpen}
                                onClose={handleColumnFilterClose}
                                slotProps={{
                                    paper: {
                                        sx: {
                                            width: 170,
                                            maxHeight: 300,
                                            backgroundColor: themeStyles.topBar.bg
                                        }
                                    }
                                }}
                            >
                                <div className="flex items-center gap-2 px-[16px] py-[6px] w-full">
                                    <input
                                        type="checkbox"
                                        className="form-checkbox cursor-pointer"
                                        checked={selectedColumns?.length === initialColumns?.length ? true : false}
                                        onClick={toggleAll}
                                    />
                                    <span className={`${themeStyles.topBar.tailwindTextColor} font-medium text-[12px]`}>Select All</span>
                                </div>
                                <Divider className="w-full" />
                                {
                                    initialColumns?.map((column, index) => (
                                        <>
                                            <MenuItem
                                                key={index}
                                                sx={{
                                                    fontSize: "12px",
                                                    marginBlock: "0px",
                                                    "&: hover": {
                                                        cursor: "default"
                                                    }
                                                }}
                                            >
                                                <div className="flex items-center gap-2">
                                                    <input
                                                        type="checkbox"
                                                        className="form-checkbox cursor-pointer"
                                                        checked={selectedColumns.includes(column?.headerName)}
                                                        onChange={() => toggleColumn(column?.headerName || "")}
                                                    />
                                                    <span className={`${themeStyles.topBar.tailwindTextColor} font-medium`}>{column?.headerName}</span>
                                                </div>

                                            </MenuItem>
                                            <Divider className="w-full" />
                                        </>

                                    ))
                                }
                            </Menu>
                        </div>
                    </div>
                    <div className="flex items-center gap-1.5">
                        <div >
                            <button
                                className={`flex items-center justify-between py-[5px] px-[12px] gap-2.5 rounded-lg border border-solid ${themeStyles.topBar.tailwindBorderColor}`}
                                aria-controls={open ? 'basic-menu' : undefined}
                                aria-haspopup="true"
                                aria-expanded={open ? 'true' : undefined}
                                onClick={handleClick}
                            >
                                <Stack
                                    direction="row"
                                    spacing={1}
                                    alignItems={"center"}
                                    justifyContent={"center"}
                                >
                                    <span className={`${themeStyles.topBar.tailwindTextColor} text-[14px] font-medium`}>{selectedFilter}</span>
                                    {open ? <span className={`material-symbols-outlined ${themeStyles.topBar.tailwindTextColor} text-[25px] font-extralight`}>
                                        keyboard_arrow_up
                                    </span> : <span className={`material-symbols-outlined ${themeStyles.topBar.tailwindTextColor} text-[25px] font-extralight`}>
                                        keyboard_arrow_down
                                    </span>}
                                </Stack>
                            </button>

                            <Menu
                                id="basic-menu"
                                anchorEl={anchorEl}
                                open={open}
                                onClose={handleClose}
                                slotProps={{
                                    paper: {
                                        sx: {
                                            width: 170,
                                            backgroundColor: themeStyles.topBar.bg
                                        }
                                    }
                                }}
                            >
                                {

                                    filterItems?.map((filter, index) => (
                                        <>
                                            <MenuItem
                                                key={index}
                                                sx={{ fontSize: "12px", marginBlock: "0px" }}
                                                onClick={() => {
                                                    setSelectedFilter(filter.label)
                                                    setReservationFilterValue(filter.id)
                                                    setAnchorEl(null);

                                                }}>
                                                <span className={`${themeStyles.topBar.tailwindTextColor} text-[16px] font-medium`}>{filter.label}</span>
                                            </MenuItem>
                                            <Divider className="w-full" />
                                        </>

                                    ))

                                }
                            </Menu>
                        </div>
                        <div className={`flex px-[5px] py-[6px] items-center gap-2.5 rounded-lg border ${themeStyles.main.borderColor} shadow-sm`}>
                            <span className={`material-symbols-outlined text-[25px] font-light ${themeStyles.main.textSubHeadingColor}`}>
                                search
                            </span>
                            <input
                                type="text"
                                placeholder="Search..."
                                value={searchValue}
                                className={`border-none outline-none w-full ${themeStyles.main.textSubHeadingColor} bg-transparent`}
                                onChange={(e) => setSearchValue(e.target.value)}
                            />
                        </div>
                    </div>
                </div>

                <CustomDataGrid
                    filteredRows={searchTableData}
                    columns={columns?.map(col => ({
                        ...col,
                        headerClassName: themeStyles.table.headerBg,
                    }))}
                    loading={loading}
                    totalLength={totalLength}
                    paginationModel={paginationModel}
                    setPaginationModel={setPaginationModel}
                />
            </div>
        </div>
    )
}